import {useMemo, useState} from 'react'
import {useMutation} from '@apollo/client'
import {Button, Form, InputGroup} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {
  BEGIN_PASSKEY_REGISTRATION,
  COMPLETE_PASSKEY_REGISTRATION,
  DELETE_PASSKEY,
} from '../../gql/mutations/authMutations'
import {DELETE_USER_PASSKEY} from '../../gql/mutations/userMutations'
import {
  IPasskeyCredential,
  IResBeginPasskeyRegistration,
  IResCompletePasskeyRegistration,
  IResDeletePasskey,
} from '../../types/auth'
import {
  createPasskey,
  getPasskeyErrorMessage,
  getPasskeyUnavailableMessage,
  isPasskeySupported,
} from '../../helpers/passkeys'

interface PasskeySettingsPanelProps {
  credentials?: IPasskeyCredential[]
  canManageOwnPasskeys?: boolean
  canManageUserPasskeys?: boolean
  targetUserId?: string | number | null
  onUpdated?: () => void | Promise<unknown>
}

type DeleteUserPasskeyResponse = {
  deleteUserPasskeyMutation: {
    success: boolean
    errors?: {field?: string | null; message: string}[] | null
  }
}

const formatDate = (value?: string | null) => {
  if (!value) return 'Never used'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

export const PasskeySettingsPanel = ({
  credentials = [],
  canManageOwnPasskeys = false,
  canManageUserPasskeys = false,
  targetUserId,
  onUpdated,
}: PasskeySettingsPanelProps) => {
  const [passkeyName, setPasskeyName] = useState('')
  const [gqlBeginPasskeyRegistration, {loading: loadingBeginRegistration}] =
    useMutation<IResBeginPasskeyRegistration>(BEGIN_PASSKEY_REGISTRATION)
  const [gqlCompletePasskeyRegistration, {loading: loadingCompleteRegistration}] =
    useMutation<IResCompletePasskeyRegistration>(COMPLETE_PASSKEY_REGISTRATION)
  const [gqlDeletePasskey, {loading: loadingDeletePasskey}] =
    useMutation<IResDeletePasskey>(DELETE_PASSKEY)
  const [gqlDeleteUserPasskey, {loading: loadingDeleteUserPasskey}] =
    useMutation<DeleteUserPasskeyResponse>(DELETE_USER_PASSKEY)

  const passkeySupported = useMemo(() => isPasskeySupported(), [])
  const canRegisterPasskeys = canManageOwnPasskeys
  const canRevokePasskeys = canManageOwnPasskeys || canManageUserPasskeys
  const loading =
    loadingBeginRegistration ||
    loadingCompleteRegistration ||
    loadingDeletePasskey ||
    loadingDeleteUserPasskey

  const runUpdated = async () => {
    if (onUpdated) {
      await Promise.resolve(onUpdated())
    }
  }

  const handleCreatePasskey = async () => {
    if (!canRegisterPasskeys) {
      toast.error('Passkey registration is only available from the user profile.')
      return
    }

    if (!passkeySupported) {
      toast.info(getPasskeyUnavailableMessage())
      return
    }

    try {
      const beginResponse = await gqlBeginPasskeyRegistration({
        variables: {
          name: passkeyName.trim() || null,
        },
      })

      const result = beginResponse.data?.beginPasskeyRegistration
      if (!result?.success || !result.challengeId || !result.publicKeyOptions) {
        toast.error(result?.message || 'Unable to start passkey registration.')
        return
      }

      const credential = await createPasskey(result.publicKeyOptions)
      const completeResponse = await gqlCompletePasskeyRegistration({
        variables: {
          challengeId: result.challengeId,
          credential,
        },
      })

      const completeResult = completeResponse.data?.completePasskeyRegistration
      if (!completeResult?.success) {
        toast.error(completeResult?.message || 'Unable to finish passkey registration.')
        return
      }

      toast.success(completeResult.message || 'Passkey registered successfully.')
      setPasskeyName('')
      await runUpdated()
    } catch (error) {
      toast.error(getPasskeyErrorMessage(error, 'Unable to create the passkey.'))
    }
  }

  const handleDeletePasskey = async (passkey: IPasskeyCredential) => {
    if (!canRevokePasskeys) {
      toast.error('You do not have permission to manage passkeys for this account.')
      return
    }

    const actionLabel = canManageUserPasskeys && !canManageOwnPasskeys ? 'Revoke' : 'Remove'
    const confirmed = window.confirm(
      `${actionLabel} passkey "${passkey.name}" ${canManageUserPasskeys && !canManageOwnPasskeys ? 'from this user account' : 'from this account'}?`
    )
    if (!confirmed) return

    try {
      if (canManageUserPasskeys && !canManageOwnPasskeys) {
        if (!targetUserId) {
          toast.error('User not found for passkey revocation.')
          return
        }

        const response = await gqlDeleteUserPasskey({
          variables: {
            userId: targetUserId,
            passkeyId: passkey.id,
          },
        })

        const result = response.data?.deleteUserPasskeyMutation
        if (!result?.success) {
          toast.error(result?.errors?.[0]?.message || 'Unable to revoke this passkey.')
          return
        }

        toast.success('Passkey revoked successfully.')
        await runUpdated()
        return
      }

      const response = await gqlDeletePasskey({
        variables: {
          passkeyId: passkey.id,
        },
      })

      const result = response.data?.deletePasskey
      if (!result?.success) {
        toast.error(result?.message || 'Unable to remove this passkey.')
        return
      }

      toast.success(result.message || 'Passkey removed successfully.')
      await runUpdated()
    } catch (error) {
      toast.error(
        getPasskeyErrorMessage(
          error,
          canManageUserPasskeys && !canManageOwnPasskeys
            ? 'Unable to revoke this passkey.'
            : 'Unable to remove this passkey.'
        )
      )
    }
  }

  return (
    <div>
      <div className='d-flex justify-content-between align-items-start flex-wrap gap-3 mb-6'>
        <div>
          <div className='fw-bold mb-2'>Passkeys</div>
          <div className='text-muted fs-6'>
            Sign in with Face ID, Touch ID, Windows Hello, or your device PIN without typing your
            password.
          </div>
        </div>
        <span className={`badge badge-light-${credentials.length > 0 ? 'success' : 'secondary'}`}>
          {credentials.length > 0 ? `${credentials.length} registered` : 'No passkeys'}
        </span>
      </div>

      {canRegisterPasskeys && !passkeySupported && (
        <div className='alert alert-warning mb-6'>{getPasskeyUnavailableMessage()}</div>
      )}

      {canRegisterPasskeys && (
        <div className='border rounded p-5 bg-light mb-6'>
          <div className='fw-semibold mb-3'>Add a passkey</div>
          <InputGroup className='mb-3'>
            <Form.Control
              placeholder='Optional name, for example "Work laptop"'
              value={passkeyName}
              onChange={(e) => setPasskeyName(e.target.value)}
              maxLength={120}
            />
            <Button
              type='button'
              className='btn btn-primary'
              onClick={handleCreatePasskey}
              disabled={loading || !passkeySupported}
            >
              {!loading && 'Add passkey'}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </Button>
          </InputGroup>
          <div className='text-muted fs-7'>
            You can register more than one passkey so you are not locked out if a device is lost.
          </div>
        </div>
      )}

      <div className='border rounded p-5 bg-light'>
        <div className='fw-semibold mb-4'>Registered passkeys</div>

        {credentials.length === 0 && (
          <div className='text-muted fs-7'>
            {canManageUserPasskeys && !canManageOwnPasskeys
              ? 'This user has no registered passkeys.'
              : 'No passkeys have been registered for this account yet.'}
          </div>
        )}

        {credentials.map((passkey) => (
          <div
            key={passkey.id}
            className='d-flex justify-content-between align-items-start flex-wrap gap-3 border rounded bg-white p-4 mb-3'
          >
            <div>
              <div className='fw-bold text-gray-800'>{passkey.name || 'Unnamed passkey'}</div>
              <div className='text-muted fs-7'>Created: {formatDate(passkey.created)}</div>
              <div className='text-muted fs-7'>Last used: {formatDate(passkey.lastUsedAt)}</div>
              {passkey.transports && passkey.transports.length > 0 && (
                <div className='text-muted fs-7'>
                  Transports: {passkey.transports.join(', ')}
                </div>
              )}
            </div>

            <Button
              type='button'
              className='btn btn-light-danger'
              onClick={() => handleDeletePasskey(passkey)}
              disabled={loadingDeletePasskey || loadingDeleteUserPasskey}
            >
              {canManageUserPasskeys && !canManageOwnPasskeys ? 'Revoke' : 'Remove'}
            </Button>
          </div>
        ))}
      </div>

      {canManageUserPasskeys && !canManageOwnPasskeys && (
        <div className='text-muted fs-7 mt-4'>
          Administrators can revoke lost or compromised passkeys, but only the user can register a
          new passkey from their own profile.
        </div>
      )}
    </div>
  )
}
